/**
 * GENERATED_ASSET — the subset of the approved Q01 static kit (`static-kit/source/{construction,geometry,validation}.ts`)
 * that the batch005 domestic builders call: SVG serializer, bounds and closed-input validators. Bodies are the
 * reviewed source unchanged; only the import graph is narrowed to what this package uses. Pure, browser-safe.
 */
import type { Point2 as Point, VisualBounds as Bounds, VisualAnchor, Tokens } from './types';

// ---------------------------------------------------------------- construction.ts
export const n=(v:number):string=>{if(!Number.isFinite(v))throw new Error('nonfinite geometry');return String(Math.round(v*1000)/1000);};
export const esc=(v:string):string=>v.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
export function el(tag:string,attrs:Record<string,string|number|undefined>,body=''):string {
  const a=Object.entries(attrs).filter(([,v])=>v!==undefined).map(([k,v])=>`${k}="${typeof v==='number'?n(v):esc(v!)}"`).join(' ');
  return body?`<${tag} ${a}>${body}</${tag}>`:`<${tag} ${a}/>`;
}
export const anchor=(name:string,x:number,y:number,role:VisualAnchor['role']='attachment'):VisualAnchor=>({name,point:{x,y},role});

// ---------------------------------------------------------------- geometry.ts
export type Matrix = readonly [number,number,number,number,number,number];
export function box(points: readonly Point[], pad = 0): Bounds {
  if (!points.length || !points.every(p=>Number.isFinite(p.x)&&Number.isFinite(p.y))) throw new Error('invalid bound points');
  const x=Math.min(...points.map(p=>p.x))-pad,y=Math.min(...points.map(p=>p.y))-pad;
  return {x,y,width:Math.max(...points.map(p=>p.x))+pad-x,height:Math.max(...points.map(p=>p.y))+pad-y};
}
export const corners=(b:Bounds):Point[]=>[{x:b.x,y:b.y},{x:b.x+b.width,y:b.y},{x:b.x,y:b.y+b.height},{x:b.x+b.width,y:b.y+b.height}];
export const expand=(b:Bounds,p:number):Bounds=>({x:b.x-p,y:b.y-p,width:b.width+2*p,height:b.height+2*p});
export const union=(b:readonly Bounds[]):Bounds=>box(b.flatMap(corners));
export const apply=(m:Matrix,p:Point):Point=>({x:m[0]*p.x+m[2]*p.y+m[4],y:m[1]*p.x+m[3]*p.y+m[5]});
export const transformBounds=(m:Matrix,b:Bounds):Bounds=>box(corners(b).map(p=>apply(m,p)));

// ---------------------------------------------------------------- validation.ts
export const ROLES = ['outline','outlineSoft','paperBase','paperLight','paperShadow','warmCore',
  'woodBase','woodLight','woodShadow','clothBlue','clothBlueLight','clothBlueShadow',
  'reedBase','reedLight','reedShadow','stoneBase','stoneLight','stoneShadow','moss','salt','waterFar','waterLight'] as const;
export function record(v: unknown, label: string): Record<string, unknown> {
  if (!v || typeof v !== 'object' || Array.isArray(v)) throw new Error(`${label}: expected object`);
  return v as Record<string, unknown>;
}
export function keys(v: Record<string, unknown>, allowed: readonly string[], label: string): void {
  for (const k of Object.keys(v)) if (!allowed.includes(k)) throw new Error(`${label}: unknown key ${k}`);
  for (const k of allowed) if (!(k in v)) throw new Error(`${label}: missing ${k}`);
}
export function one(v: unknown, values: readonly unknown[], label: string): void {
  if (!values.includes(v)) throw new Error(`${label}: unsupported value ${String(v)}`);
}
export function range(v: unknown, min: number, max: number, label: string, integer = false): number {
  if (typeof v !== 'number' || !Number.isFinite(v) || v < min || v > max || (integer && !Number.isInteger(v)))
    throw new Error(`${label}: expected ${integer ? 'integer ' : ''}${min}..${max}`);
  return v;
}
export function validateTokens(raw: unknown): Tokens {
  const t = record(raw,'tokens'), palettes = record(t.palettes,'palettes');
  if (typeof t.revision !== 'string' || !t.revision) throw new Error('tokens revision required');
  keys(palettes,['coastal_salt','nook_warm','icon_ink'],'palettes');
  for (const [id,v] of Object.entries(palettes)) {
    const p = record(v,id); keys(p,ROLES,id);
    for (const [role,c] of Object.entries(p)) if (typeof c !== 'string' || !/^#[0-9a-f]{6}$/i.test(c)) throw new Error(`invalid material ${id}.${role}`);
  }
  const s = record(t.style,'style');
  keys(s,['outerStroke','seamStroke','grainStroke','grainOpacity','contactShadowOpacity','coordinatePrecision','samplingPadding'],'style');
  for (const k of ['outerStroke','seamStroke','grainStroke']) range(s[k],.1,4,k);
  for (const k of ['grainOpacity','contactShadowOpacity']) range(s[k],0,.5,k);
  one(s.coordinatePrecision,[3],'coordinatePrecision'); range(s.samplingPadding,1,8,'samplingPadding');
  const m=record(t.masterMotif,'motif');
  if (JSON.stringify(m.centers) !== JSON.stringify([{x:-12,y:-56},{x:12,y:-56},{x:0,y:-32}]) || m.radius !== 7.5 || m.protectedRadius !== 10 || m.randomization !== 'forbidden') throw new Error('master two-over-one motif is immutable');
  const tiers=record(t.detailTiers,'tiers'); keys(tiers,['readable','standard','close'],'tiers');
  for (const v of Object.values(tiers)) { const d=record(v,'tier'); range(d.grainMarks,0,20,'grainMarks',true); if(typeof d.minorRepairs!=='boolean') throw new Error('minorRepairs boolean required'); }
  return raw as Tokens;
}
